// AirPlay-only sender (iter 126 — reverted to iter-114 Safari-only after
// iter 125's Chromium Remote Playback addition broke casting: hls.js
// attaches via MSE on Chromium, and Chromecast receivers can't decode a
// MediaSource stream without a custom Cast receiver app registered under
// a Google Cast Application ID. The button appeared, picker connected,
// but the receiver only showed the poster — "visuals but no video."
//
// The right way to add Chromecast is: register an App ID with Google,
// build a receiver app that speaks HLS, then re-add the Remote Playback
// path with the receiver ID wired through. Deferred until real demand.
//
// This file exposes ONE hook: `useAirPlay(videoRef, key)` returning
// `{ available, wireless, show }`. No-op on any non-Safari browser.

import { useEffect, useState, useCallback } from 'react';

export function useAirPlay(videoRef, key) {
  const [available, setAvailable] = useState(false);
  const [wireless, setWireless] = useState(false);

  useEffect(() => {
    const v = videoRef?.current;
    if (!v) return undefined;
    if (typeof v.webkitShowPlaybackTargetPicker !== 'function') return undefined;

    try { v.setAttribute('x-webkit-airplay', 'allow'); } catch (_e) { /* attr set may fail — ignore */ }

    const onAvail = (e) => setAvailable(e.availability === 'available');
    const onWirelessChange = () => {
      try { setWireless(!!v.webkitCurrentPlaybackTargetIsWireless); } catch (_e) { /* property read guarded */ }
    };
    onWirelessChange();
    v.addEventListener('webkitplaybacktargetavailabilitychanged', onAvail);
    v.addEventListener('webkitcurrentplaybacktargetiswirelesschanged', onWirelessChange);
    return () => {
      v.removeEventListener('webkitplaybacktargetavailabilitychanged', onAvail);
      v.removeEventListener('webkitcurrentplaybacktargetiswirelesschanged', onWirelessChange);
    };
  }, [videoRef, key]);

  const show = useCallback(() => {
    const v = videoRef?.current;
    if (v && typeof v.webkitShowPlaybackTargetPicker === 'function') {
      try { v.webkitShowPlaybackTargetPicker(); } catch (_e) { /* picker rejection is not fatal */ }
    }
  }, [videoRef]);

  return { available, wireless, show };
}
